import { factorIdMap } from 'pages/ResultsOverview/constants';
import { AnswersItem, Maybe, Question, QuestionItem } from './__generated__/graphql';

export const allQuestionsAnswered = (questions: Question, answers: AnswersItem) => {
  const personality = checkQuestionsAnswered(questions?.personality || [], answers);
  const interests = checkQuestionsAnswered(questions?.interests || [], answers);
  const values = checkQuestionsAnswered(questions?.values || [], answers);
  return personality && interests && values;
};

export const checkQuestionsAnswered = (questions: Maybe<Maybe<QuestionItem>[] | undefined>, answers: Maybe<AnswersItem>) => {
  // If no questions provided or questions is undefined/null, return false (not complete)
  if (!questions || questions.length === 0) return false;

  // If no answers at all, definitely not complete
  if (!answers || Object.keys(answers).length === 0) return false;

  const questionIds = questions?.map((question) => question?.id);
  const checkAllKeys = questionIds?.every((id: Maybe<string> | undefined) => {
    if (answers && id) {
      return isBoolean(answers[id as keyof AnswersItem]);
    } else return false;
  });
  return checkAllKeys;
};

export const isAnswerEmpty = (obj: Maybe<AnswersItem>) => {
  if (!obj) {
    return true;
  } else {
    return Object.keys(obj).length === 0;
  }
};

export const isBoolean = (val: string | boolean | null | undefined) => {
  return val === false || val === true;
};

/**
 * This method computes the mean over all the provided answer IDs for which answers can
 * be found in the answers object.
 * @param factor the name of the factor we're computing the mean for.
 * @param answers the object containing the answers.
 * @param questions the original questions object, which contains the polarity we need.
 */
export const meanOf = (factor: string, answers: Maybe<AnswersItem>, questions: Maybe<QuestionItem>[]) => {
  const factorIds = factorIdMap[factor];
  if (!factorIds) {
    return null;
  }

  const answerIds = questions.filter((q) => q?.id && factorIds.includes(q.id)).map((q) => q?.id);

  if (answerIds.length === 0) return null;

  if (!answers || Object.values(answers).every((v) => !v)) {
    return null;
  }

  const accumulatedValue = answerIds.reduce((acc, currId) => {
    let multiplier: Maybe<number> | undefined = 0;
    const currQuestion = questions.filter((q) => q?.id === currId);
    if (currQuestion.length > 0) {
      multiplier = currQuestion[0]?.polarity;
    } else {
      console.log('no polarity for question found!', currId);
    }

    let currentMultipliedValue = 0;
    if (answers && currId && multiplier && currId in answers) {
      const currentAnswer = answers[currId as keyof AnswersItem];
      const currentInt = currentAnswer ? 1 : -1;
      currentMultipliedValue = currentInt * multiplier;
    }
    return acc + currentMultipliedValue;
  }, 0);

  const minusOnePlusOne = accumulatedValue / answerIds.length;
  const percentage = ((minusOnePlusOne + 1) / 2) * 100;
  return percentage;
};

export const capitalizeTitle = (str: string): string => {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// New function to check if any questions in a section have been answered
export const hasAnsweredQuestionsInSection = (questions: Maybe<Maybe<QuestionItem>[] | undefined>, answers: Maybe<AnswersItem>) => {
  if (!questions || questions.length === 0 || !answers) return false;

  const questionIds = questions?.map((question) => question?.id);
  return questionIds?.some((id: Maybe<string> | undefined) => {
    if (answers && id) {
      return isBoolean(answers[id as keyof AnswersItem]);
    }
    return false;
  });
};

// New function to get the completion percentage of a section
export const getSectionCompletionPercentage = (questions: Maybe<Maybe<QuestionItem>[] | undefined>, answers: Maybe<AnswersItem>) => {
  if (!questions || questions.length === 0) return 100;
  if (!answers) return 0;

  const questionIds = questions?.map((question) => question?.id);
  const answeredCount =
    questionIds?.filter((id: Maybe<string> | undefined) => {
      if (answers && id) {
        return isBoolean(answers[id as keyof AnswersItem]);
      }
      return false;
    }).length || 0;

  return Math.round((answeredCount / questionIds.length) * 100);
};

// Debugging utilities for testing the left off functionality
export const debugAnswersState = (questions: Maybe<Question>, answers: Maybe<AnswersItem>) => {
  if (!questions || !answers) {
    console.log('Missing questions or answers data');
    return;
  }

  console.log('=== Debug Answers State ===');

  const sections = ['values', 'interests', 'personality'] as const;

  sections.forEach((section) => {
    const sectionQuestions = questions[section];
    const sectionAnswered = checkQuestionsAnswered(sectionQuestions, answers);
    const sectionStarted = hasAnsweredQuestionsInSection(sectionQuestions, answers);
    const completionPercentage = getSectionCompletionPercentage(sectionQuestions, answers);

    console.log(`${section.toUpperCase()}:`, {
      totalQuestions: sectionQuestions?.length || 0,
      completed: sectionAnswered,
      started: sectionStarted,
      completionPercentage: `${completionPercentage}%`,
    });

    // Show which specific questions are answered/unanswered
    if (sectionQuestions) {
      const questionStatus = sectionQuestions.map((q) => {
        const questionId = q?.id;
        const answerValue = questionId ? answers[questionId as keyof AnswersItem] : undefined;
        return {
          id: questionId,
          answered: isBoolean(answerValue),
          value: answerValue,
        };
      });
      console.log(`${section} question details:`, questionStatus);
    }
  });

  console.log('=== End Debug ===');
};

// Test utility to create mock answer states for testing
export const createTestAnswerState = (scenario: 'empty' | 'values-partial' | 'values-complete' | 'interests-partial' | 'mixed-null-boolean') => {
  const baseAnswers: AnswersItem = {
    // Values questions (yv prefix)
    yv2: null,
    yv4: null,
    yv5: null,
    yv8: null,
    yv12: null,
    yv21a: null,
    yv23: null,
    yv26: null,
    yv28: null,
    yv29: null,
    yv32: null,
    yv33: null,
    yv34: null,
    yv37: null,
    yv38: null,

    // Interests questions (ip prefix)
    ip1: null,
    ip2: null,
    ip3: null,
    ip4: null,
    ip5: null,
    ip6: null,
    ip7: null,
    ip8: null,
    ip9: null,
    ip10: null,
    ip11: null,
    ip12: null,
    ip13: null,
    ip14: null,
    ip15: null,
    ip16: null,
    ip17: null,
    ip18: null,
    ip19: null,
    ip20: null,
    ip21: null,
    ip22: null,
    ip23: null,
    ip24: null,
    ip25: null,
    ip26: null,
    ip27: null,
    ip28: null,
    ip29: null,
    ip30: null,

    // Personality questions (p prefix)
    p1a: null,
    p2: null,
    p3: null,
    p4: null,
    p5: null,
    p6: null,
    p7: null,
    p8: null,
    p9: null,
    p10: null,
    p11: null,
    p12: null,
    p13: null,
    p14: null,
    p15a: null,
    p16a: null,
    p17: null,
    p18: null,
    p19a: null,
    p20b: null,
  };

  switch (scenario) {
    case 'empty':
      return baseAnswers;

    case 'values-partial':
      return {
        ...baseAnswers,
        yv2: true,
        yv4: false,
        yv5: true,
        // yv8 and others remain null
      };

    case 'values-complete':
      return {
        ...baseAnswers,
        yv2: true,
        yv4: false,
        yv5: true,
        yv8: false,
        yv12: true,
        yv21a: false,
        yv23: true,
        yv26: false,
        yv28: true,
        yv29: false,
        yv32: true,
        yv33: false,
        yv34: true,
        yv37: false,
        yv38: true,
      };

    case 'interests-partial':
      return {
        ...baseAnswers,
        // Complete values first
        yv2: true,
        yv4: false,
        yv5: true,
        yv8: false,
        yv12: true,
        yv21a: false,
        yv23: true,
        yv26: false,
        yv28: true,
        yv29: false,
        yv32: true,
        yv33: false,
        yv34: true,
        yv37: false,
        yv38: true,
        // Partial interests
        ip1: true,
        ip2: false,
        ip3: true,
        // ip4 and others remain null
      };

    case 'mixed-null-boolean':
      return {
        ...baseAnswers,
        // This simulates the problematic state you described
        yv2: true,
        yv4: null, // This should be boolean but is null
        yv5: false,
        yv8: null, // This should be boolean but is null
        yv12: true,
        // Some interests answered (which shouldn't happen if values aren't complete)
        ip1: true,
        ip2: false,
        ip3: null,
      };

    default:
      return baseAnswers;
  }
};
